// Copilot — the sidebar ideation chat. The user brainstorms mobile-first
// PWA ideas with a fast, cheap model (Haiku, server-side via the gateway
// /v1/copilot/chat proxy). Deliberately simple: plain text, no streaming.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Sparkles, ArrowUp, RotateCw, PenSquare, ChevronRight, Wand2, Layers, Pencil, ArrowUpRight, RotateCcw } from 'lucide-preact';
import { AppPreview } from '../components/AppPreview';
import { getRecentApps, type RecentApp } from '../lib/recent-apps';
import { t } from '../lib/i18n';

// The model is instructed to end each idea with a paste-ready line prefixed
// exactly "Build prompt: " (workers/generation-worker COPILOT_SYSTEM). We parse
// those out to offer a REAL one-tap handoff into the existing build pipeline.
function extractBuildPrompts(content: string): string[] {
  const out: string[] = [];
  const re = /^\s*Build prompt:\s*(.+?)\s*$/gim;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content))) {
    const s = (m[1] || '').replace(/[*_`"]/g, '').trim();
    if (s) out.push(s);
  }
  return out;
}

// REAL handoff — the SAME seed-prompt bridge the Sidebar starters + Workspace
// duplicate flow use (useGenerationSession reads 'vs:creator:seed-prompt' on Home
// mount). No duplicated generation logic: Pomy hands the brief to the live builder.
function buildFromPrompt(prompt: string): void {
  try { sessionStorage.setItem('vs:creator:seed-prompt', prompt); } catch { /* private mode */ }
  navigator.vibrate?.(10);
  location.hash = '';
}

// REAL handoff — open an existing app into the builder's done-state (where the
// live preview + version restore + Magic Wand + revise all work).
function openInBuilder(appId: string): void {
  try { sessionStorage.setItem('vs:creator:open-app', appId); } catch { /* private mode */ }
  navigator.vibrate?.(8);
  location.hash = '';
}

interface Msg {
  role: 'user' | 'assistant';
  content: string;
  /**
   * A stable per-message id, assigned when the message is appended. The
   * thread renderer uses it to decide which assistant turn should play the
   * soft fade-in animation: only the most-recently-arrived assistant turn
   * carries the `.cp-prose--fade-in` class, so re-renders never re-trigger
   * the animation on older turns.
   */
  id: string;
}

function newMsgId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// 2-column starter cards — solved-problem framing rather than generic prompts.
// Each card describes the *outcome* so users can pick an inspiring direction,
// not a blank-page prompt. `prompt` is sent to the model; `title` and `desc`
// are display-only.
//
// Phase-2: Pomi is now a Success Partner, not just an ideator. The card
// mix balances idea-starters with support-style entry points ("I'm stuck"
// "Help me make it more elegant") so the chat surface answers the full
// range of questions a builder actually has.
// i18n key triples (title / desc / prompt) — resolved via t() at render so the
// active locale wins (a Hebrew user gets Hebrew starters + Hebrew briefs).
// LAUNCH HONESTY: a starter that pitches a concrete APP idea must map to a
// genuinely-SUPPORTED category (category-support.ts) — `category` names it. The
// old "trip planner" (trip-planners, PLANNED) and "book club voting app"
// (community, PLANNED) advertised unsupported experiences and were replaced with
// a flashcard deck + a workout log. `category: null` marks a META/ideation card
// (help me pick / I'm stuck / make it elegant) that pitches no specific category.
// Guarded by tests/launch-honesty-guard.test.mjs.
const STARTERS: Array<{ titleKey: string; descKey: string; promptKey: string; category: string | null }> = [
  { titleKey: 'cop.s1t', descKey: 'cop.s1d', promptKey: 'cop.s1p', category: 'trackers' },
  { titleKey: 'cop.s2t', descKey: 'cop.s2d', promptKey: 'cop.s2p', category: 'flashcards' },
  { titleKey: 'cop.s3t', descKey: 'cop.s3d', promptKey: 'cop.s3p', category: 'workout-logs' },
  { titleKey: 'cop.s4t', descKey: 'cop.s4d', promptKey: 'cop.s4p', category: null },
  { titleKey: 'cop.s5t', descKey: 'cop.s5d', promptKey: 'cop.s5p', category: null },
  { titleKey: 'cop.s6t', descKey: 'cop.s6d', promptKey: 'cop.s6p', category: null }
];

// Quick-action chips above the composer — three frequent intents one
// tap away. These don't pre-fill the input (the chips below the
// composer would feel cluttered); they send the message directly so
// "I'm stuck" lands with the assistant immediately.
const QUICK_ACTIONS: Array<{ label: string; prompt: string }> = [
  { label: t('cop.qa1'), prompt: 'I want to build something fresh today. Ask me 3 questions about what I’ve been wishing existed, then suggest 3 specific app ideas.' },
  { label: t('cop.qa2'), prompt: 'I’m stuck. I’m not sure what to build or how to phrase my prompt. Help me get unstuck, what’s the smallest first move I can make right now?' },
  { label: t('cop.qa3'), prompt: 'I have a rough prompt idea. Ask me what it is, then rewrite it as a tight, vivid, generation-ready brief.' }
];

// LocalStorage key for chat persistence. Persists across tab close + reload
// so Pomi can pick up where you left off the next time you open the chat —
// the conversation is the long-running thread, not the tab. The "New chat"
// button explicitly clears this so a fresh start is one tap away.
const CHAT_STORAGE_KEY = 'pomela:copilot:thread:v2';

export function Copilot(props: { onClose: () => void }): JSX.Element {
  // Restore prior thread from localStorage so Pomi remembers the
  // conversation across tab closes and reloads. Lazy initializer runs once.
  const [messages, setMessages] = useState<Msg[]>(() => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(CHAT_STORAGE_KEY) : null;
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter((m) => m && typeof m.content === 'string') as Msg[];
    } catch {}
    return [];
  });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Persist the thread on every change so Pomi remembers across reload
  // and tab close. The "New chat" button below clears this explicitly.
  useEffect(() => {
    try {
      if (typeof localStorage === 'undefined') return;
      if (messages.length === 0) localStorage.removeItem(CHAT_STORAGE_KEY);
      else localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
    } catch {}
  }, [messages]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  /**
   * Send `thread` to the copilot and append the reply. Split from `send`
   * so Retry can re-run the exact last thread without re-appending the
   * user message (it's already in `messages`).
   */
  async function ask(thread: Msg[]): Promise<void> {
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/v1/copilot/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ messages: thread })
      });
      if (res.status === 429) {
        // Rate limited — surface the precise wait time from Retry-After.
        const retryAfter = Number(res.headers.get('Retry-After'));
        const wait =
          Number.isFinite(retryAfter) && retryAfter > 0
            ? t('cop.errBusyWait', { n: retryAfter })
            : t('cop.errBusyMoment');
        setError(wait);
        return;
      }
      if (res.status === 401 || res.status === 403) {
        setError(t('cop.errAuth'));
        return;
      }
      if (res.status >= 500) {
        setError(t('cop.errServer'));
        return;
      }
      if (!res.ok) { setError(t('cop.errGeneric')); return; }
      const data = (await res.json()) as { reply?: string };
      setMessages([
        ...thread,
        { role: 'assistant', content: data.reply?.trim() || '…', id: newMsgId() }
      ]);
    } catch {
      // Network / runtime failure — distinct from a server-side rate limit
      // so we can hint at connectivity instead of asking the user to wait.
      setError(t('cop.errNetwork'));
    } finally {
      setBusy(false);
    }
  }

  async function send(text: string): Promise<void> {
    const content = text.trim();
    if (!content || busy) return;
    navigator.vibrate?.(8);
    const next: Msg[] = [...messages, { role: 'user', content, id: newMsgId() }];
    setMessages(next);
    setInput('');
    await ask(next);
  }

  /** Re-send the current thread — used by the error-state Retry button. */
  function retry(): void {
    if (busy || messages.length === 0) return;
    navigator.vibrate?.(8);
    void ask(messages);
  }

  /** Clear the conversation and return to the starter intro. */
  function newChat(): void {
    if (busy) return;
    navigator.vibrate?.(8);
    setMessages([]);
    setInput('');
    setError('');
  }

  function onKeyDown(e: JSX.TargetedKeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      void send(input);
    }
  }

  const canSend = !busy && input.trim().length > 0;
  const hasChat = messages.length > 0;
  // The id of the most-recent assistant turn — used to scope the soft
  // fade-in animation to ONLY the freshly-arrived reply, so a re-render
  // (e.g. on input change) never replays the animation on older turns.
  const latestAssistantId =
    [...messages].reverse().find((m) => m.role === 'assistant')?.id ?? '';

  return (
    <div className="cp-screen" dir="auto">
      {/* atlas-aligned chrome — pf-topbar (brand spark + Pomy + New chat),
          consistent with Settings/Account/Guide. The mobile tab bar
          replaces the legacy hamburger→sidebar. */}
      <div className="pf-topbar">
        <span aria-hidden="true" style={{ display: 'grid', placeItems: 'center', width: 30, height: 30, borderRadius: 9, background: 'var(--grad)', color: '#fff', flex: '0 0 auto' }}>
          <Sparkles size={15} strokeWidth={2.25} />
        </span>
        <span className="pf-topbar__title">{t('sb.copilot.title')}</span>
        <span className="pf-topbar__spacer" />
        {hasChat && (
          <button type="button" className="pf-btn pf-btn--ghost pf-btn--sm" onClick={newChat} disabled={busy}>
            <PenSquare size={14} /> {t('cop.newChat')}
          </button>
        )}
      </div>

      <div className="cp-workspace">
        {/* LEFT — the real Pomy ideation chat */}
        <div className="cp-chatpane">
          <div ref={scrollRef} className="cp-scroll">
            <div className="cp-thread">
              {messages.length === 0 && !busy ? (
                <Intro onPick={(s) => void send(s)} />
              ) : (
                messages.map((m) => (
                  <Turn key={m.id} msg={m} freshAssistant={m.id === latestAssistantId} onBuild={buildFromPrompt} />
                ))
              )}
              {busy && <Thinking />}
              {error && (
                <div className="cp-error" role="alert">
                  <p className="cp-error__line">{error}</p>
                  {hasChat && (
                    <button type="button" className="cp-retry" onClick={retry} disabled={busy}>
                      <RotateCw size={13} />
                      Retry
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="cp-composer">
            <div className="cp-composer__inner">
              <input
                value={input}
                onInput={(e) => setInput((e.target as HTMLInputElement).value)}
                onKeyDown={onKeyDown}
                placeholder={t('cop.placeholder')}
                aria-label={t('cop.ariaMessage')}
                maxLength={1000}
                dir="auto"
                className="cp-input"
              />
              <button
                type="button"
                className="cp-send"
                onClick={() => void send(input)}
                disabled={!canSend}
                aria-label={t('cop.ariaSend')}
              >
                <ArrowUp size={18} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT — real build context: latest app live preview + real version
            timeline + handoff into the existing builder (desktop only). */}
        <BuildContext />
      </div>

    </div>
  );
}

/**
 * The real build-context pane. Reads the creator's own recent apps (the same
 * localStorage timeline the builder writes) and shows the latest app's LIVE
 * preview + its REAL version timeline + handoffs into the existing builder /
 * Workspace. No generation logic is duplicated here; everything is a view of,
 * or a handoff to, systems that already exist.
 */
function BuildContext(): JSX.Element {
  const apps = useMemo(() => getRecentApps(), []);
  const app: RecentApp | undefined = apps[0];

  if (!app) {
    return (
      <aside className="cp-buildpane" aria-label={t('cop.latestBuild')}>
        <div className="pf-empty" style={{ marginTop: 4, background: 'var(--hero-wash), var(--bg-el)' }}>
          <span className="pf-bubble pf-mark-halo" aria-hidden="true"><Wand2 size={22} /></span>
          <p className="pf-empty__title">{t('cop.buildEmptyT')}</p>
          <p className="pf-empty__lede">{t('cop.buildEmptyS')}</p>
        </div>
      </aside>
    );
  }

  const versions = app.versions ?? [];
  const name = app.manifest?.name || firstLine(app.prompt) || 'Your app';

  return (
    <aside className="cp-buildpane" aria-label={t('cop.latestBuild')}>
      <span className="pf-sect__h" style={{ margin: 0 }}>{t('cop.latestBuild')}</span>
      <div className="cp-build-preview"><AppPreview appId={app.appId} url={app.url} ogImageUrl={app.ogImageUrl} /></div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
        <span style={{ fontSize: 11.5, color: 'var(--fg-sub)' }}>
          {versions.length} {versions.length === 1 ? t('cop.versionN') : t('cop.versionsN')} · {t('cop.localOnly')}
        </span>
      </div>

      {versions.length > 0 && (
        <div>
          <span className="pf-sect__h">{t('cop.timelineTitle')}</span>
          {[...versions].reverse().slice(0, 4).map((v, i, arr) => {
            const idx = versions.length - 1 - i; // original numbering
            const title = v.kind === 'create' ? t('cop.original') : `Revision ${idx}`;
            return (
              <div key={v.appId + v.at} className="cp-build-vrow">
                <span className="pf-lthumb pf-lthumb--accent" style={{ width: 26, height: 26 }} aria-hidden="true">
                  {v.kind === 'create' ? <Sparkles size={12} /> : <Pencil size={12} />}
                </span>
                <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
                  <span style={{ fontSize: 11, color: 'var(--fg-sub)' }}>{relTime(v.at)}</span>
                </span>
                {i === 0 && arr.length > 1 ? null : null}
              </div>
            );
          })}
          <p style={{ fontSize: 11, color: 'var(--fg-sub)', margin: '6px 2px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
            <RotateCcw size={11} /> {t('cop.restoreHint')}
          </p>
        </div>
      )}

      <div className="cp-build-actions">
        <button type="button" className="pf-btn pf-btn--grad pf-btn--block" onClick={() => openInBuilder(app.appId)}>
          <Layers size={14} /> {t('cop.openBuilder')}
        </button>
        <button type="button" className="pf-btn pf-btn--block" onClick={() => { location.hash = `apps/${encodeURIComponent(app.appId)}`; }}>
          <ArrowUpRight size={14} /> {t('cop.openWorkspace')}
        </button>
      </div>
    </aside>
  );
}

function firstLine(s: string): string {
  const line = (s || '').split('\n')[0]?.trim() || '';
  return line.length > 60 ? line.slice(0, 57) + '…' : line;
}

function relTime(ms: number): string {
  const sec = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (sec < 45) return 'just now';
  const m = Math.round(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function Intro({ onPick }: { onPick: (s: string) => void }): JSX.Element {
  return (
    <div className="cp-intro">
      {/* Pomi the Success Partner — a warmer hello than the original
          "Let's brainstorm" headline. The avatar carries the brand
          gradient so the conversation starts with the same chrome the
          assistant turns will use later in the thread. */}
      <div className="pomi-intro-v2">
        <span className="pomi-avatar pomi-avatar--lg breath" aria-hidden="true">
          <Sparkles size={24} strokeWidth={2} />
        </span>
        <h2 className="pomi-intro-v2__greet">{t('cop.greet')}</h2>
        <p className="pomi-intro-v2__sub">
          {t('cop.intro')}
        </p>
      </div>
      <div className="cp-starters">
        {STARTERS.map((s) => (
          <button
            key={s.titleKey}
            type="button"
            className="cp-starter"
            onClick={() => onPick(t(s.promptKey))}
          >
            <span className="cp-starter__icon" aria-hidden="true">
              <Sparkles size={15} />
            </span>
            <span className="cp-starter__body">
              <span className="cp-starter__title">{t(s.titleKey)}</span>
              <span className="cp-starter__desc">{t(s.descKey)}</span>
            </span>
            <ChevronRight size={15} className="cp-starter__chev" />
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Tiny inline-markdown renderer for assistant prose. Handles **bold**,
 * *italic*, `inline-code`, [link](url), and turns blank-line-separated
 * paragraphs and `- ` lists into structured DOM. Deliberately small (no
 * dependency, no XSS surface — we use only text nodes + safe elements
 * with no `dangerouslySetInnerHTML`).
 */
function renderMarkdown(text: string): JSX.Element[] {
  const blocks: JSX.Element[] = [];
  const paragraphs = text.split(/\n\s*\n/);
  paragraphs.forEach((para, pi) => {
    const lines = para.split('\n');
    // Bullet list — any block whose lines all start with "- " or "* ".
    if (lines.every((l) => /^\s*[-*]\s+/.test(l)) && lines.length > 0) {
      blocks.push(
        <ul key={`p${pi}`}>
          {lines.map((l, li) => (
            <li key={li}>{renderInline(l.replace(/^\s*[-*]\s+/, ''))}</li>
          ))}
        </ul>
      );
      return;
    }
    // Numbered list — `1. ` `2. ` etc.
    if (lines.every((l) => /^\s*\d+\.\s+/.test(l)) && lines.length > 0) {
      blocks.push(
        <ol key={`p${pi}`}>
          {lines.map((l, li) => (
            <li key={li}>{renderInline(l.replace(/^\s*\d+\.\s+/, ''))}</li>
          ))}
        </ol>
      );
      return;
    }
    blocks.push(<p key={`p${pi}`}>{renderInline(para)}</p>);
  });
  return blocks;
}

/** Inline markdown: **bold**, *italic*, `code`, [text](url). XSS-safe
 *  because every captured value becomes a text node, never raw HTML. */
function renderInline(text: string): (string | JSX.Element)[] {
  // Tokenizer: walk through one regex match at a time, splice surrounding
  // text as plain strings. Order: code (greediest) → bold → italic → link.
  const out: (string | JSX.Element)[] = [];
  const pattern = /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = pattern.exec(text))) {
    if (m.index > lastIndex) out.push(text.slice(lastIndex, m.index));
    if (m[1] !== undefined) {
      out.push(<code key={`md${key++}`}>{m[1]}</code>);
    } else if (m[2] !== undefined) {
      out.push(<strong key={`md${key++}`}>{m[2]}</strong>);
    } else if (m[3] !== undefined) {
      out.push(<em key={`md${key++}`}>{m[3]}</em>);
    } else if (m[4] !== undefined && m[5] !== undefined) {
      const href = m[5];
      // Only allow http(s) and mailto: hrefs. Anything else (javascript:,
      // data:, vbscript:) renders as plain text.
      const isSafe = /^https?:\/\//i.test(href) || /^mailto:/i.test(href);
      if (isSafe) {
        out.push(
          <a key={`md${key++}`} href={href} target="_blank" rel="noreferrer noopener">
            {m[4]}
          </a>
        );
      } else {
        out.push(m[4]);
      }
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
}

/**
 * A single conversation turn, assistant renders as prose with a small
 * gradient sparkle avatar (Claude pattern), user renders as a right-aligned
 * pink bubble. Flex `flex-end`/`flex-start` keeps the alignment
 * direction-aware so RTL works untouched. Assistant prose runs through
 * a tiny markdown renderer for **bold**, *italic*, `code`, and lists —
 * massive readability upgrade over plain text.
 */
function Turn({ msg, freshAssistant, onBuild }: { msg: Msg; freshAssistant: boolean; onBuild: (prompt: string) => void }): JSX.Element {
  const mine = msg.role === 'user';
  if (mine) {
    return (
      <div className="cp-turn cp-turn--user">
        <div dir="auto" className="cp-user-bubble">
          {msg.content}
        </div>
      </div>
    );
  }
  // Only the most-recently-added assistant turn carries the fade-in class.
  // Older turns paint statically on re-render so the animation never plays
  // twice for the same message.
  const proseClass = freshAssistant ? 'cp-prose cp-prose--fade-in' : 'cp-prose';
  // REAL "Build this" handoff: any paste-ready "Build prompt:" line the model
  // emitted becomes a one-tap bridge into the live builder.
  const builds = extractBuildPrompts(msg.content);
  return (
    <div className="cp-turn cp-turn--assistant">
      <span className="pomi-avatar" aria-hidden="true">
        <Sparkles size={13} strokeWidth={2.25} aria-hidden="true" />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div dir="auto" className={proseClass}>
          {renderMarkdown(msg.content)}
        </div>
        {builds.length > 0 && (
          <div className="cp-buildrow">
            {builds.map((p, i) => (
              <button key={i} type="button" className="pf-btn pf-btn--grad pf-btn--sm" title={p} onClick={() => onBuild(p)}>
                <Wand2 size={13} /> {t('cop.buildThis')}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Thinking(): JSX.Element {
  return (
    <div className="cp-turn cp-turn--assistant" aria-label={t('cop.ariaThinking')}>
      <span className="pomi-avatar pomi-avatar--thinking" aria-hidden="true">
        <Sparkles size={13} strokeWidth={2.25} aria-hidden="true" />
      </span>
      <div className="cp-thinking" role="status">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}
